/**
 * Main-thread consumer of quadratic-core wasm profiling samples.
 *
 * The core worker drains the wasm profiler buffer (see `worker/corePerf.ts`) and
 * emits `wasmPerfMetrics` batches. This module aggregates those samples per
 * exported function over a rolling window and:
 *   - logs a summary table to the console when the `debugWasmPerf` flag is on,
 *   - periodically reports an aggregated summary to Mixpanel (sampled), and
 *   - leaves Sentry breadcrumbs for slow/erroring functions so they show up as
 *     context on any subsequent error report.
 *
 * Nothing here runs unless the wasm bundle was built with the `core-profiler`
 * cargo feature, because no samples are ever emitted otherwise.
 */

import { debugFlag } from '@/app/debugFlags/debugFlags';
import { events } from '@/app/events/events';
import type { CorePerfMetric } from '@/app/web-workers/quadraticCore/coreClientMessages';
import { trackEvent } from '@/shared/utils/analyticsEvents';
import { addBreadcrumb } from '@sentry/react';

// How often to roll up the accumulated window and report it.
const FLUSH_INTERVAL_MS = 30_000;

// Per-call duration above which a function is considered slow enough to leave a
// Sentry breadcrumb / surface prominently.
const SLOW_CALL_THRESHOLD_MICROS = 250_000; // 250ms

// Number of functions (by total time) included in the Mixpanel payload to keep
// the event small.
const TOP_N = 15;

// Fraction of sessions that report aggregated metrics to Mixpanel. Decided once
// per session to keep a given session's data internally consistent.
//
// This is a Mixpanel event-volume lever, NOT a client-overhead one: data
// collection in wasm is always 100% and costs the same regardless. We report
// every session so perf distributions stay unbiased. If event volume ever
// becomes a concern (e.g. once the profiler is compiled into the production
// build), prefer widening FLUSH_INTERVAL_MS over dropping whole sessions here.
const TELEMETRY_SAMPLE_RATE = 1;

interface Accumulator {
  count: number;
  errorCount: number;
  totalMicros: number;
  minMicros: number;
  maxMicros: number;
  maxMemoryDeltaBytes?: number;
}

interface FunctionSummary {
  name: string;
  count: number;
  errorCount: number;
  totalMicros: number;
  avgMicros: number;
  minMicros: number;
  maxMicros: number;
  maxMemoryDeltaBytes?: number;
}

class WasmPerfReporter {
  private initialized = false;
  private interval: ReturnType<typeof setInterval> | undefined;
  private window = new Map<string, Accumulator>();
  private windowStart = 0;
  private readonly sampledForTelemetry = Math.random() < TELEMETRY_SAMPLE_RATE;

  init = () => {
    if (this.initialized) return;
    this.initialized = true;
    this.windowStart = Date.now();

    events.on('wasmPerfMetrics', this.handleMetrics);
    this.interval = setInterval(this.flush, FLUSH_INTERVAL_MS);

    // Best-effort flush when the tab is being hidden/unloaded so the last
    // window is not lost.
    if (typeof window !== 'undefined') {
      window.addEventListener('pagehide', this.flush);
      window.addEventListener('visibilitychange', this.onVisibilityChange);
    }
  };

  // Tears down the interval and listeners registered in init(). Not needed in
  // normal operation (the reporter lives for the lifetime of the page), but
  // provided for symmetry with CorePerf.stop() and to support explicit
  // teardown (e.g. quadraticCore.terminate()) and deterministic tests.
  stop = () => {
    if (!this.initialized) return;
    this.flush();
    if (this.interval !== undefined) {
      clearInterval(this.interval);
      this.interval = undefined;
    }
    events.off('wasmPerfMetrics', this.handleMetrics);
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', this.flush);
      window.removeEventListener('visibilitychange', this.onVisibilityChange);
    }
    this.initialized = false;
  };

  private onVisibilityChange = () => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      this.flush();
    }
  };

  private handleMetrics = (metrics: CorePerfMetric[]) => {
    if (debugFlag('debugWasmPerf')) {
      // Live view of each drained batch, in addition to the rolled-up summary
      // emitted on flush. Shows raw per-call rows as they arrive.
      console.groupCollapsed(`[wasmPerf] +${metrics.length} calls`);
      console.table(
        metrics.map((m) => ({
          fn: m.name,
          ok: m.ok,
          ms: (m.duration_micros / 1000).toFixed(3),
          'mem Δ KB': m.memory_delta_bytes !== undefined ? (m.memory_delta_bytes / 1024).toFixed(1) : '',
          'in B': m.input_bytes ?? '',
          'out B': m.output_bytes ?? '',
        }))
      );
      console.groupEnd();
    }

    for (const metric of metrics) {
      const acc = this.window.get(metric.name) ?? {
        count: 0,
        errorCount: 0,
        totalMicros: 0,
        minMicros: Number.POSITIVE_INFINITY,
        maxMicros: 0,
      };
      acc.count += 1;
      if (!metric.ok) acc.errorCount += 1;
      acc.totalMicros += metric.duration_micros;
      acc.minMicros = Math.min(acc.minMicros, metric.duration_micros);
      acc.maxMicros = Math.max(acc.maxMicros, metric.duration_micros);
      if (metric.memory_delta_bytes !== undefined) {
        // We only track the largest growth. The delta is derived from
        // wasm `memory.size`, which is monotonic (linear memory never
        // shrinks), so memory_delta_bytes is effectively always >= 0 and the
        // `?? 0` floor never discards a real (negative) measurement.
        acc.maxMemoryDeltaBytes = Math.max(acc.maxMemoryDeltaBytes ?? 0, metric.memory_delta_bytes);
      }
      this.window.set(metric.name, acc);
    }
  };

  private buildSummaries = (): FunctionSummary[] => {
    const summaries: FunctionSummary[] = [];
    for (const [name, acc] of this.window) {
      summaries.push({
        name,
        count: acc.count,
        errorCount: acc.errorCount,
        totalMicros: acc.totalMicros,
        avgMicros: Math.round(acc.totalMicros / acc.count),
        minMicros: acc.minMicros === Number.POSITIVE_INFINITY ? 0 : acc.minMicros,
        maxMicros: acc.maxMicros,
        maxMemoryDeltaBytes: acc.maxMemoryDeltaBytes,
      });
    }
    summaries.sort((a, b) => b.totalMicros - a.totalMicros);
    return summaries;
  };

  flush = () => {
    if (this.window.size === 0) return;

    const summaries = this.buildSummaries();
    const windowMs = Date.now() - this.windowStart;
    const totalCalls = summaries.reduce((sum, s) => sum + s.count, 0);
    const totalErrors = summaries.reduce((sum, s) => sum + s.errorCount, 0);

    if (debugFlag('debugWasmPerf')) {
      console.log(`[wasmPerf] ${totalCalls} calls / ${totalErrors} errors over ${windowMs}ms`);
      console.table(
        summaries.map((s) => ({
          fn: s.name,
          count: s.count,
          errors: s.errorCount,
          'avg ms': (s.avgMicros / 1000).toFixed(3),
          'max ms': (s.maxMicros / 1000).toFixed(3),
          'total ms': (s.totalMicros / 1000).toFixed(3),
          'max mem Δ KB': s.maxMemoryDeltaBytes !== undefined ? (s.maxMemoryDeltaBytes / 1024).toFixed(1) : '',
        }))
      );
    }

    const slow = summaries.filter((s) => s.maxMicros >= SLOW_CALL_THRESHOLD_MICROS || s.errorCount > 0);
    if (slow.length > 0) {
      addBreadcrumb({
        category: 'wasm-perf',
        level: 'info',
        message: 'quadratic-core slow/erroring wasm calls',
        data: {
          windowMs,
          functions: slow.slice(0, TOP_N).map((s) => ({
            name: s.name,
            count: s.count,
            errors: s.errorCount,
            maxMs: Math.round(s.maxMicros / 1000),
            avgMs: Math.round(s.avgMicros / 1000),
          })),
        },
      });
    }

    if (this.sampledForTelemetry) {
      trackEvent('[Core].wasmPerf', {
        windowMs,
        totalCalls,
        totalErrors,
        functions: summaries.slice(0, TOP_N).map((s) => ({
          name: s.name,
          count: s.count,
          errorCount: s.errorCount,
          avgMicros: s.avgMicros,
          maxMicros: s.maxMicros,
          totalMicros: s.totalMicros,
          maxMemoryDeltaBytes: s.maxMemoryDeltaBytes,
        })),
      });
    }

    this.window.clear();
    this.windowStart = Date.now();
  };
}

export const wasmPerfReporter = new WasmPerfReporter();
